import React, { useState } from 'react';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { useAuth } from '../../auth';
import { useNavigate } from 'react-router-dom';
import { useGallery, type GalleryItem } from '../hooks/useGallery';
import { GalleryCard } from '../components/GalleryCard';
import { GalleryFilter } from '../components/GalleryFilter';
import { 
  Image, 
  Upload, 
  Video, 
  AlertCircle,
  Plus,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { SEO } from '../../../shared/components';

const GalleryPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { galleryItems, loading, error, filters, setFilters, deleteGalleryItem } = useGallery(user?.role === 'admin');
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleView = (item: GalleryItem) => {
    setSelectedItem(item);
    setCurrentIndex(galleryItems.findIndex(i => i.id === item.id));
  };

  const handleEdit = (id: number) => {
    navigate(`/gallery/admin/edit/${id}`);
  };

  const handleDelete = async (id: number, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteGalleryItem(id);
      } catch (err) {
        console.error('Error deleting gallery item:', err);
        alert('Failed to delete gallery item. Please try again.');
      }
    }
  };

  const closeModal = () => {
    setSelectedItem(null);
  };

  const navigateModal = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % galleryItems.length
      : (currentIndex - 1 + galleryItems.length) % galleryItems.length;
    
    setCurrentIndex(newIndex);
    setSelectedItem(galleryItems[newIndex]);
  };

  return (
    <>
      <SEO
        title={user?.role === 'admin' ? 'Gallery Management - Admin Panel' : 'Gallery - Tuition Point'}
        description={user?.role === 'admin' ? 'Manage gallery images and videos' : 'View our photo and video gallery'}
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Image className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    {user?.role === 'admin' ? 'Gallery Management' : 'Our Gallery'}
                  </h1>
                  <p className="mt-1 text-purple-100 text-sm">
                    {user?.role === 'admin' 
                      ? 'Manage images and videos displayed on the homepage'
                      : 'Explore our collection of memorable moments and activities'
                    }
                  </p>
                </div>
              </div>
              
              {user?.role === 'admin' && (
                <Button
                  onClick={() => navigate('/gallery/admin/upload')}
                  className="bg-white text-purple-600 hover:bg-purple-50 shadow-md"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Upload New Items
                </Button>
              )}
            </div>
          </div>

          {/* Gallery Section */}
          <Card className="border-t-4 border-t-purple-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-purple-100 to-violet-100 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Image className="h-5 w-5 text-purple-600" />
                </div>
                Gallery Items
              </CardTitle>
              <CardDescription className="mt-1.5">
                {user?.role === 'admin' 
                  ? 'All uploaded images and videos with management options'
                  : 'Our collection of photos and videos'
                }
              </CardDescription>
            </div>
            
            <CardContent className="p-6">
              {/* Filter Bar */}
              <div className="mb-6">
                <GalleryFilter 
                  filters={filters} 
                  setFilters={setFilters} 
                  showAdminFilters={user?.role === 'admin'}
                />
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Gallery Grid */}
              <div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1,2,3,4,5,6,7,8].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="bg-gray-200 aspect-square rounded-lg mb-3"></div>
                        <div className="bg-gray-200 h-4 rounded mb-2"></div>
                        <div className="bg-gray-200 h-3 rounded w-2/3"></div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {galleryItems.map((item) => (
                      <GalleryCard 
                        key={item.id} 
                        item={item}
                        isAdmin={user?.role === 'admin'}
                        onView={handleView}
                        onEdit={user?.role === 'admin' ? handleEdit : undefined}
                        onDelete={user?.role === 'admin' ? handleDelete : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>

              {!loading && galleryItems.length === 0 && (
                <div className="text-center py-12">
                  <Image className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">
                    No gallery items available
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {user?.role === 'admin' 
                      ? 'Start by uploading your first images or videos.'
                      : 'Check back soon for new photos and videos!'
                    }
                  </p>
                  {user?.role === 'admin' && (
                    <Button
                      onClick={() => navigate('/gallery/admin/upload')}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload First Items
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <Button
              onClick={closeModal}
              size="sm"
              variant="outline"
              className="absolute top-4 right-4 z-10 bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </Button>

            {/* Navigation Buttons */}
            {galleryItems.length > 1 && (
              <>
                <Button
                  onClick={() => navigateModal('prev')}
                  size="sm"
                  variant="outline"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => navigateModal('next')}
                  size="sm"
                  variant="outline"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}

            {/* Content */}
            <div className="w-full h-full flex flex-col">
              <div className="flex-1 flex items-center justify-center">
                {selectedItem.fileType === 'image' ? (
                  <img 
                    src={selectedItem.url} 
                    alt={selectedItem.title}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <video 
                    src={selectedItem.url}
                    controls
                    className="max-w-full max-h-full"
                    autoPlay
                  />
                )}
              </div>
              
              {/* Info */}
              <div className="bg-black/50 backdrop-blur-sm p-4 text-center">
                <h3 className="text-white font-semibold text-lg">{selectedItem.title}</h3>
                {selectedItem.description && (
                  <p className="text-white/80 text-sm mt-1">{selectedItem.description}</p>
                )}
                <div className="flex items-center justify-center gap-4 mt-2 text-xs text-white/60">
                  <span className="flex items-center gap-1">
                    {selectedItem.fileType === 'image' ? (
                      <Image className="h-3 w-3" />
                    ) : (
                      <Video className="h-3 w-3" />
                    )}
                    {selectedItem.fileType.toUpperCase()}
                  </span>
                  <span>{new Date(selectedItem.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryPage;
